<template>
  <div class="component__container">
    <div class="out__container" @click.stop="hasFocus = false"
         :class="{ out__active: isActivate }"></div>
    <div class="input__container"
         :class="{ component__active: isActivate, error__value: hasError }"
         @click.stop="onClick">
      <input class="input__custom" type="text" :class="{ error__value: hasError }" :value="getValue" readonly/>
      <label class="component__label"
             :class="{ mini__label: hasValue || isActivate, error__value: hasError }"
      >
        {{ label }}
      </label>
      <i v-if="clearable && hasValue"
         @click.stop="onDeleteValue"
         class="mdi mdi-close"
         :class="{ error__value: hasError }"
      ></i>
      <i class="mdi mdi-menu-down" :class="{ error__value: hasError }"></i>
    </div>
    <div class="input__values" :class="{ values__visible: isActivate }">
      <ul v-if="items.length">
        <li v-for="(item, index) in items"
            :key="index"
            @click.stop="onSelect(item[itemValue])"
        >{{ item[itemText] }}
        </li>
      </ul>
      <ul v-else class="no__data">
        <li @click.stop="onCloseModal">{{ noDataText }}</li>
      </ul>
    </div>
    <div class="error__message" :class="{ error__value: hasError }">
      <p v-for="(errorMessage, index) in errorMessages"
         :class="{ error__value: hasError }"
         :key="index"
      >{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSelect',
  props: {
    clearable: {
      required: false,
      type: Boolean,
      default: false
    },
    errorMessages: {
      required: false,
      type: [Array, String],
      default: ''
    },
    items: {
      required: true,
      type: Array
    },
    itemValue: {
      required: false,
      type: String,
      default: 'value'
    },
    itemText: {
      required: false,
      type: String,
      default: 'text'
    },
    label: {
      required: false,
      type: String,
      default: ''
    },
    noDataText: {
      required: false,
      type: String,
      default: ''
    },
    value: {
      required: false,
      default: ''
    }
  },
  data () {
    return {
      hasError: false,
      hasFocus: false
    }
  },
  computed: {
    isActivate () {
      return this.hasFocus
    },
    hasValue () {
      return Boolean(this.value)
    },
    getValue () {
      return this.hasValue
        ? this.items.find(item => item[this.itemValue] === this.value)[this.itemText]
        : ''
    }
  },
  watch: {
    errorMessages (newValue) {
      this.hasError = typeof newValue === 'object'
        ? newValue.length !== 0
        : newValue !== ''
    }
  },
  methods: {
    onClick () {
      if (this.hasFocus) {
        this.$el.querySelector('input')
          .blur()
        this.hasFocus = false
      } else {
        this.$el.querySelector('input')
          .focus()
        this.hasFocus = true
      }
    },
    onCloseModal () {
      this.$el.querySelector('input')
        .blur()
      this.hasFocus = false
    },
    onDeleteValue () {
      this.$emit('input', '')
      this.$emit('change', '')
    },
    onSelect (value) {
      this.$emit('input', value)
      this.$emit('change', value)
      this.hasFocus = false
    }
  }
}
</script>

<style lang="scss" scoped>
  $mainColor: #47A4A9;
  $notActiveColor: rgb(115, 115, 115);
  $hoverColor: rgb(55, 55, 55);
  $valueHoverColor: rgba(214, 214, 214, 0.3);
  $valueActiveColor: rgba(140, 140, 140, 0.3);
  $errorColor: rgb(247, 82, 83);

  $transitionTime: 300ms;

  .component__container {
    position: relative;
    z-index: 200;
    padding: 12px 0;

    .out__container {
      width: 100vw;
      height: 100vh;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 100;
      display: none;

      &.out__active {
        display: block;
      }
    }

    .input__container {
      position: relative;
      display: flex;
      cursor: pointer;
      z-index: 500;
      min-height: 32px;

      &::after {
        content: "";
        width: 100%;
        height: 1px;
        background-color: $notActiveColor;
        position: absolute;
        bottom: 0;
        left: 0;
        transition: background-color 500ms;
      }

      &:hover::after {
        background-color: $hoverColor;
      }

      &.error__value::after {
        background-color: $errorColor;
      }

      &::before {
        content: "";
        width: 0;
        height: 2px;
        background-color: $mainColor;
        position: absolute;
        bottom: 0;
        left: 50%;
        transition: left $transitionTime, width $transitionTime;
      }

      &.component__active::before {
        width: 100%;
        left: 0;
      }

      input {
        width: 100%;
        outline: none;
        cursor: pointer;
        border: none;
        background-color: rgba(255, 255, 255, 0);

        &.error__value {
          color: $errorColor;
        }
      }

      label {
        position: absolute;
        z-index: -1;
        color: $notActiveColor;
        top: 7px;
        left: 0;
        transition: color $transitionTime, transform $transitionTime;

        &.mini__label {
          color: $mainColor;
          transform-origin: 0 0;
          transform: translateY(-18px) scale(0.75);
        }

        &.error__value {
          color: $errorColor;
        }
      }

      i {
        font-family: 'Material Icons', sans-serif;
        font-size: 24px;
        font-style: normal;
        color: $notActiveColor;
        transition: color $transitionTime, transform $transitionTime;

        &.error__value {
          color: $errorColor;
        }
      }

      input:focus ~ i {
        color: $mainColor;
        transform: rotate(180deg);
      }
    }

    .input__values {
      box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
      0 8px 10px 1px rgba(0, 0, 0, 0.14),
      0 3px 14px 2px rgba(0, 0, 0, 0.12);
      display: none;
      z-index: 2000;
      position: absolute;
      width: 100%;
      background-color: white;

      &.values__visible {
        display: block;
      }

      ul {
        padding: 0;
        box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
        0 2px 2px 0 rgba(0, 0, 0, 0.14),
        0 1px 5px 0 rgba(0, 0, 0, 0.12);
        z-index: 500;
        margin: 0;

        li {
          list-style: none;
          cursor: pointer;
          padding: 12px;
          position: relative;
          z-index: 500;

          &:hover {
            background-color: $valueHoverColor;
          }

          &:active {
            background: $valueActiveColor;
          }
        }
      }

      ul.no__data {
        li {
          font-style: italic;
          cursor: not-allowed;

          &:hover {
            background-color: white;
          }
        }
      }
    }

    .error__message {
      margin-top: 5px;

      p {
        color: $errorColor;
        font-size: 0.75em;
      }
    }
  }
</style>
